import React, { useState, useEffect, useRef } from "react";
import { useTheme } from '../../context/ThemeContext';

function LogViewer({ url, close, idx, label }) {
  const { darkMode } = useTheme();
  const [logContent, setLogContent] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const logContainerRef = useRef(null);

  useEffect(() => {
    const fetchLog = async () => {
      try {
        const response = await fetch(url);
        const text = await response.text();
        setLogContent(text);
      } catch (error) {
        console.error("Error fetching log:", error);
        setLogContent("Error loading log file.");
      }
    };

    fetchLog();
  }, [url]);

  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    setHighlightedIndex(-1);

    // Disable highlighting if search term is less than 3 characters
    if (term.length < 3) {
      setSearchTerm("");
    }
  };

  // Optimized highlightMatches function (using split and join)
  const highlightMatches = (text, term) => {
    if (!term) return text;
    return text
      .split(new RegExp(`(${term})`, "gi"))
      .map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : part));
  };

  const scrollToMatch = (index) => {
    if (logContainerRef.current) {
      const highlightedElements =
        logContainerRef.current.querySelectorAll("mark");
      if (highlightedElements[index]) {
        highlightedElements[index].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };

  const handleNextMatch = () => {
    setHighlightedIndex((prevIndex) => {
      const nextIndex = prevIndex + 1;
      const highlightedElements =
        logContainerRef.current.querySelectorAll("mark");
      if (nextIndex < highlightedElements.length) {
        scrollToMatch(nextIndex);
        return nextIndex;
      }
      return prevIndex; // Stay at the last match
    });
  };

  const handlePreviousMatch = () => {
    setHighlightedIndex((prevIndex) => {
      const newIndex = prevIndex - 1; // Changed prevIndex to newIndex
      if (newIndex >= 0) {
        scrollToMatch(newIndex);
        return newIndex;
      }
      return 0;
    });
  };

  // Calculate number of matches
  const numberOfMatches =
    logContent.split(new RegExp(`(${searchTerm})`, "gi")).length - 1;

  return (
    <div className={`modal is-active is-rounded`} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999,
    }}>
      <div className="modal-background" onClick={close} style={{
        opacity: '0.5',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}></div>
      <div className={`modal-card is-rounded ${darkMode ? 'has-background-dark' : 'has-background-light'}`} 
        style={{
          borderRadius: '15px',
          maxWidth: '80vw',
          width: '100%',
          margin: '2rem auto',
          maxHeight: 'calc(100vh - 4rem)',
          position: 'relative'
        }}>
        <header className={`modal-card-head is-rounded ${darkMode ? 'has-background-grey-darker' : 'has-background-light'}`} style={{borderRadius: '15px'}}>
          <p className={`modal-card-title ${darkMode ? 'has-text-white' : 'has-text-dark'}`}>
            Log Viewer - {label} (chap {idx * 1 + 1})
          </p>
          <button 
            className="delete" 
            aria-label="close" 
            onClick={close}
          ></button>
        </header>

        <section className={`modal-card-body is-rounded ${darkMode ? 'has-background-dark' : 'has-background-light'}`}>
          <input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            className={`input mb-3 ${darkMode ? 'is-dark' : 'is-light'}`}
            onChange={handleSearchChange}
          />
          
          <div className="mb-3 is-flex is-justify-content-space-between">
            <button
              onClick={handlePreviousMatch}
              disabled={highlightedIndex <= 0}
              className={`button is-small ${darkMode ? 'is-dark' : 'is-light'}`}>
              &lt;
            </button>
            <span className={darkMode ? 'has-text-white' : 'has-text-dark'}>
              {highlightedIndex + 1} of {numberOfMatches} matches
            </span>
            <button
              onClick={handleNextMatch}
              disabled={highlightedIndex >= numberOfMatches - 1}
              className={`button is-small ${darkMode ? 'is-dark' : 'is-light'}`}>
              &gt;
            </button>
          </div>

          <div
            ref={logContainerRef}
            className={`${darkMode ? 'has-background-black' : 'has-background-white-ter'}`}
            style={{
              maxHeight: '60vh',
              maxWidth: '100%',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              textWrap: 'wrap',
              lineBreak: 'anywhere',
              padding: '1rem',
              borderRadius: '4px'
            }}>
            <pre
              style={{
                maxWidth: '100%',
                overflow: 'hidden',
                whiteSpace: 'pre-wrap',
                textWrap: 'wrap',
                lineBreak: 'anywhere',
              }}
              className={darkMode ? 'has-text-white' : 'has-text-dark'}>
              {highlightMatches(logContent, searchTerm)}
            </pre>
          </div>
        </section>
      </div>
    </div>
  );
}

export default LogViewer;
